// Dashboard 8 — Conversion Attribution (last-touch, 24h heuristic). Headline:
// of all sessions, how many were message-driven vs organic return. Message-
// driven broken down by the channel that drove it, plus conversion-type counts.

import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Skeleton } from "../../../ui/skeleton";
import { StatCard, RankedBars } from "../../../Components/admin/reporting/primitives";
import { Panel } from "../../../Components/admin/reporting/controls";
import { getEngagementAttribution } from "../../../Services/adminEngagement";
import {
  CHANNEL_LABELS,
  CONVERSION_TYPE_LABELS,
  RESERVED_CONVERSION_TYPES,
  ErrorBox,
  EmptyState,
  WindowBadge,
  fmtInt,
  fmtDuration,
  useEngagementData,
} from "./engagementShared";

const DONUT_COLORS = ["#2563eb", "#cbd5e1"]; // message-driven, organic

const AttributionTab = ({ range }) => {
  const { data, loading, error } = useEngagementData(
    () => getEngagementAttribution(range),
    [range]
  );

  if (error) return <ErrorBox>{error}</ErrorBox>;
  if (loading && !data) {
    return (
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!data) return <EmptyState>No attribution data available.</EmptyState>;

  const sessions = data.sessions || {};
  const total = sessions.total || 0;
  const driven = sessions.message_driven || 0;
  const organic = sessions.organic_return || 0;
  const drivenPct = total ? Math.round((driven / total) * 100) : 0;

  const donutData = [
    { name: "Message-driven", value: driven },
    { name: "Organic return", value: organic },
  ];

  const triggers = data.by_trigger_source || {};
  const triggerBars = Object.entries(triggers)
    .sort((a, b) => (b[1]?.attributed_conversions || 0) - (a[1]?.attributed_conversions || 0))
    .map(([ch, v]) => ({
      label: CHANNEL_LABELS[ch] || ch,
      count: v.attributed_conversions || 0,
      suffix: `~${fmtDuration(v.avg_time_to_convert_sec)}`,
    }));

  const convTypes = data.by_conversion_type || {};
  const convEntries = Object.keys(convTypes).filter((k) => convTypes[k] != null);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <WindowBadge window={data.window} timezone={data.timezone} />
        <span className="text-xs text-muted-foreground">
          Last-touch attribution · {data.attribution_window_hours ?? 24}h window.
        </span>
      </div>

      {/* Headline */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <StatCard label="Total sessions" value={fmtInt(total)} />
        <StatCard
          label="Message-driven"
          value={fmtInt(driven)}
          sub={`${drivenPct}% of all sessions`}
        />
        <StatCard
          label="Organic return"
          value={fmtInt(organic)}
          sub={`${100 - drivenPct}% of all sessions`}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Donut */}
        <Panel title="Message-driven vs organic">
          <div className="flex items-center gap-4">
            <div className="relative" style={{ width: 200, height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    startAngle={90}
                    endAngle={-270}
                  >
                    {donutData.map((_, i) => (
                      <Cell key={i} fill={DONUT_COLORS[i]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtInt(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold tabular-nums">{drivenPct}%</span>
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  message-driven
                </span>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              {donutData.map((d, i) => (
                <div key={d.name} className="flex items-center gap-2">
                  <span
                    className="h-3 w-3 rounded-sm"
                    style={{ backgroundColor: DONUT_COLORS[i] }}
                  />
                  <span className="text-muted-foreground">{d.name}</span>
                  <span className="font-semibold tabular-nums">{fmtInt(d.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </Panel>

        {/* Trigger source */}
        <Panel title="Attributed conversions by trigger channel">
          <RankedBars
            items={triggerBars}
            color="bg-sky-500/70"
            emptyLabel="No attributed conversions in this window."
          />
          {triggerBars.length ? (
            <p className="mt-3 text-[11px] text-muted-foreground">
              Suffix shows average open→conversion latency.
            </p>
          ) : null}
        </Panel>
      </div>

      {/* Conversion types */}
      <Panel title="Attributed conversions by type">
        {convEntries.length === 0 ? (
          <EmptyState>No conversions in this window.</EmptyState>
        ) : (
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
            {convEntries.map((k) => {
              const reserved = RESERVED_CONVERSION_TYPES.has(k);
              return (
                <div key={k} className="flex items-baseline justify-between gap-2">
                  <span
                    className={
                      reserved
                        ? "text-xs text-muted-foreground/60"
                        : "text-xs text-muted-foreground"
                    }
                  >
                    {CONVERSION_TYPE_LABELS[k] || k}
                    {reserved ? (
                      <span className="ml-1 text-[10px] italic">(reserved)</span>
                    ) : null}
                  </span>
                  <span
                    className={
                      reserved
                        ? "text-sm font-medium tabular-nums text-muted-foreground/60"
                        : "text-lg font-semibold tabular-nums"
                    }
                  >
                    {fmtInt(convTypes[k])}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
};

export default AttributionTab;
